/**
 * StoreBar 共通组件
 * 店铺选择栏，包含所属店铺、收藏店铺、其他店铺的下拉选择
 */

import React, { FC, useEffect, useState, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../../store/reducers';
import { fetchCommonClubListRequest } from '../../../store/actions/storeBar/storeBarActions';
import { StoreBarProps } from './StoreBar.types';

const StoreBar: FC<StoreBarProps> = ({ selectedClubId, onClubSelect, onSetBelongClubName }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  // 从Redux获取店铺列表
  const { belongClubs, favoriteClubs, otherClubs, loading } = useSelector((state: RootState) => state.storeBar);

  // 从Redux获取用户所属店铺ID
  const { homeDatas } = useSelector((state: RootState) => state.home);

  // 本地状态：当前选中的店铺ID
  const [currentClubId, setCurrentClubId] = useState<string>('');

  // 本地状态：显示用的所属店铺列表（包含homeDatas当belongClubs为空时）
  const [displayBelongClubs, setDisplayBelongClubs] = useState<any[]>([]);

  // 当belongClubs或homeDatas变化时更新显示用的所属店铺列表
  useEffect(() => {
    if (belongClubs.length > 0) {
      setDisplayBelongClubs(belongClubs);
    } else if (homeDatas.shop_id && homeDatas.shop_id !== 0 && homeDatas.shop_name) {
      setDisplayBelongClubs([
        {
          club_id: homeDatas.shop_id,
          club_name: homeDatas.shop_name.replaceAll('MIRA fitness ', ''),
          pre_open_day: homeDatas.pre_open_day,
        },
      ]);
    } else {
      setDisplayBelongClubs([]);
    }
  }, [belongClubs, homeDatas.shop_id, homeDatas.shop_name]);

  /**
   * 组件挂载时获取店铺列表
   */
  useEffect(() => {
    const memberID = sessionStorage.getItem('memberID');
    if (memberID) {
      dispatch(fetchCommonClubListRequest(memberID));
    }
  }, [dispatch]);

  useEffect(() => {
    if (displayBelongClubs.length > 0 && onSetBelongClubName) {
      onSetBelongClubName(displayBelongClubs[0].club_name, displayBelongClubs[0].pre_open_day);
    }
  }, [displayBelongClubs, homeDatas.shop_name]);

  /**
   * 当有用户所属店铺时，设置为默认选中
   */
  useEffect(() => {
    if (selectedClubId) {
      setCurrentClubId(selectedClubId.toString());
    } else if (homeDatas.shop_id && homeDatas.shop_id !== 0) {
      setCurrentClubId(homeDatas.shop_id.toString());
    } else if (displayBelongClubs.length > 0) {
      setCurrentClubId(displayBelongClubs[0].club_id.toString());
    }
  }, [selectedClubId, homeDatas.shop_id, displayBelongClubs]);

  /**
   * 下拉选择变更处理
   */
  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const clubId = e.target.value;
    setCurrentClubId(clubId);

    // 如果有回调函数，则调用
    if (onClubSelect && clubId) {
      const allClubs = [...displayBelongClubs, ...favoriteClubs, ...otherClubs];
      const selectedClub = allClubs.find((club) => club.club_id.toString() === clubId);
      if (selectedClub) {
        onClubSelect(selectedClub.club_id, selectedClub.club_name, selectedClub.pre_open_day);
      }
    }
  };

  /**
   * 点击搜索按钮，跳转到预约页面
   */
  const handleSearchClick = () => {
    history.push('/reservation');
  };

  // 如果正在加载，显示加载中
  if (loading) {
    return (
      <section className="p-store-bar">
        <div className="p-store-bar__container">
          <div className="c-borderless-selector">
            <select name="store" className="c-borderless-selector__window" disabled>
              <option>読み込み中...</option>
            </select>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="p-store-bar">
      <div className="p-store-bar__container">
        <div className="c-borderless-selector">
          <select
            name="store"
            className="c-borderless-selector__window"
            value={currentClubId}
            onChange={handleSelectChange}
          >
            <option value="" hidden>
              店舗を選択する
            </option>

            {/* 所属店舗グループ */}
            {displayBelongClubs.length > 0 && (
              <optgroup label="所属店舗">
                {displayBelongClubs.map((club) => (
                  <option key={club.club_id} value={club.club_id}>
                    {club.club_name}
                  </option>
                ))}
              </optgroup>
            )}

            {/* お気に入りグループ */}
            {favoriteClubs.length > 0 && (
              <optgroup label="お気に入り">
                {favoriteClubs.map((club) => (
                  <option key={club.club_id} value={club.club_id}>
                    {club.club_name}
                  </option>
                ))}
              </optgroup>
            )}

            {/* その他の店舗グループ */}
            {otherClubs.length > 0 && (
              <optgroup label="その他の店舗">
                {otherClubs.map((club) => (
                  <option key={club.club_id} value={club.club_id}>
                    {club.club_name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
        <button
          type="button"
          className="c-button c-button--primary c-button--x-small c-button--search-right u-gap5"
          onClick={handleSearchClick}
        >
          他の店舗を探す
        </button>
      </div>
    </section>
  );
};

export default StoreBar;
